import type {
  Access,
  AccessRequestOf,
  ArgsOf,
  Delegate,
  ListedKeysOf,
  Output,
  Parameter,
  PermissionOf,
  RequestOf,
  SpecToType,
  StagedRequestOf,
} from "../access/types.ts";
import { MS } from "../time.ts";
import type { Evidence } from "./evidence.ts";
import type { Lister, SelectItem } from "./lister.ts";
import type { IntegrationResourceRule } from "./workflow/types.ts";

export type EmptyAccess = Access<
  Record<string, Output>,
  Record<string, Parameter>,
  Record<string, Delegate<any, any>>
>;

export type EmptyAccesses = Record<string, EmptyAccess>;

export const DEFAULT_LIFECYCLE_MAX_RETRIES = 2;

export const DEFAULT_LIFECYCLE_RETRY_INTERVAL = 500 * MS;

/** Sentinel for generated properties that are unused in the access lifecycle */
export const UNUSED_STAGED_ITEM = "_unused";

export type RequestData = {
  principal: string;
  requestId: string;
};

export type NewRequestData = Omit<RequestData, "requestId"> & {
  requestor: string;
};

/**
 * A function that normalizes input arguments into a request object
 */
export type NormalizeRequestFunction<Access extends EmptyAccess> = (
  args: ArgsOf<Access>,
  request: NewRequestData
) => Promise<PermissionOf<Access>[]>;

/**
 * A function that takes an un-staged request and returns a staged request with generated data
 */
export type StageRequestFunction<Access extends EmptyAccess> =
  LifecycleFunction<Access, SpecToType<Access["generated"]>>;

/**
 * A function that provisions an atomic infrastructure change for a given access step.
 */
type UpFunction<
  Access extends EmptyAccess,
  Current extends object,
  Next extends Current,
> = (
  request: StagedRequestOf<Access>,
  locals: Current,
  identity: string
) => Promise<Next>;

/**
 * A function that de-provisions an atomic infrastructure change for a given access step.
 */
type DownFunction<Access extends EmptyAccess> = (
  request: StagedRequestOf<Access>,
  identity: string
) => Promise<void>;

/** Specifies the error action to take when an error occurs during a lifecycle step
 *
 * - `abort`: Abort the lifecycle step and propagate the error
 * - `retry`: Retry the lifecycle step (default behavior)
 * - `ignore`: Ignore the error and continue
 */
export type ErrorAction = "abort" | "ignore" | "retry";

/**
 * A function that recovers from errors.
 *
 * @returns next state if the error is acceptable or {@link ErrorAction} otherwise
 */
export type UpErrorBoundaryFunction<Current extends object, Next = Current> = (
  error: unknown,
  current: Current
) => Promise<Exclude<ErrorAction, "ignore"> | Next>;

/**
 * A function that recovers from errors.
 *
 * @returns `ignore` if the error is acceptable or an {@link ErrorAction} otherwise
 */
export type DownErrorBoundaryFunction = (
  error: unknown
) => Promise<ErrorAction>;

/**
 * A function that defines a series of steps to be taken to grant and revoke access.
 */
export type StepsFunction<Access extends EmptyAccess> = (
  start: StepChain<Access, object, object>
) => StepChain<Access, any, any>;

type LifecycleFunction<Access extends EmptyAccess, Return> = (
  request: RequestOf<Access>
) => Promise<Return>;

type UpHandler<
  Access extends EmptyAccess,
  Current extends object,
  Next extends Current,
> = {
  run: UpFunction<Access, Current, Next>;
  boundary?: UpErrorBoundaryFunction<Current, Next>;
};

type DownHandler<Access extends EmptyAccess> = {
  run: DownFunction<Access>;
  boundary?: DownErrorBoundaryFunction;
};

export type StepOptions = {
  /** When 'checked', will validate idempotency in dev and stage environments
   *
   * Defaults to 'checked'; new additions of 'unchecked' will trigger an error.
   */
  idempotency?: "checked" | "unchecked";

  /** Maximum number of times this step can be retried before it is treated as
   * erroring
   */
  maxRetries?: number;
  /** Number of ms to wait between retries */
  retryInterval?: number;
};

/**
 * An atomic step that provisions and de-provisions infrastructure changes in order to grant and revoke access to a resource.
 */
export type Step<
  Access extends EmptyAccess,
  Current extends object = object,
  Next extends Current = Current,
> = {
  /** Label for this step, for use in logging */
  label: string;
  /** Provisions infrastructure changes for this access step */
  up?: UpFunction<Access, Current, Next> | UpHandler<Access, Current, Next>;
  /** Removes infrastructure changed for this access step */
  down?: DownFunction<Access> | DownHandler<Access>;
  /** Tests "acceptable" errors; use to ensure step idempotency
   */
} & StepOptions;

/** A step that does not change the context. */
export type ProcedureStep<Access extends EmptyAccess> = <
  L extends object,
>() => Step<Access, L, L>;

export type StepChain<
  Access extends EmptyAccess,
  Current extends object = object,
  Next extends Current = Current,
> = {
  /**
   * Executes the next step in the step chain.
   * @param next The next step to execute
   * @example
   * ```typescript
   * const steps = (start) => start.then({
   *      label: "Step 1",
   *      up: async (request, locals) => {
   *        // Provision infrastructure changes
   *      },
   *      down: async (request) => {
   *        // De-provision infrastructure changes
   *      }
   *    }).then({
   *      label: "Step 2",
   *      // ...
   *    })
   * ```
   */
  then: <O extends Next>(
    next: Step<Access, Next, O>
  ) => StepChain<Access, Current, O>;

  /**
   * Delegates to another lifecycle provider to execute a different step chain.
   *
   * @example
   * ```typescript
   * const steps = (start) => start.then({
   *   label: "Step 1",
   *   // ...
   * }).delegate(
   *   async () => ({
   *     provider: otherProvider,
   *     access: "otherAccess",
   *     request: (r) => ({ ...r, newField: "newField" })
   * }).then({
   *   label: "Step 2",
   *   // ...
   * })
   * ```
   *
   * @deprecated Use `delegates` in access
   */
  legacyDelegate: <
    ToContext extends object,
    ToAccesses extends Record<string, EmptyAccess>,
    ToAccessKey extends keyof ToAccesses,
  >(
    transform: OptionalLegacyDelegateTransform<
      Access,
      ToContext,
      ToAccesses,
      ToAccessKey
    >
  ) => StepChain<Access, Current, Current>;
};

export type FilterIdOf<I extends IntegrationResourceRule> =
  I extends IntegrationResourceRule
    ? keyof NonNullable<I["filters"]> & string
    : never;

export enum Listing {
  /** Lister is defined outside the lifecycle
   *
   * @deprecated Use generated lister unless caching is required
   */
  External,
  /** Lister is created via the lifecycle */
  Auto,
}

type FilterableSelectItem<
  F extends Record<string, string[] | boolean | string>,
> = {
  /** Filterable values. Each property should correspond to the corresponding rule's
   *  filter keys.
   */
  filterables?: F;
} & SelectItem;

export type AnyFilters = Record<string, string[] | boolean | string>;
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type NoFilters = {};

export type AnyFilterableSelectItem = FilterableSelectItem<AnyFilters>;

/** Defines how this parameter should be listed in calls to `ls` and in modal drop-downs */
// TODO: Add caching
export type AccessParameterListing<
  Filters extends AnyFilters,
  Access extends EmptyAccess,
> = {
  type: Listing.Auto;
  /** If present, configures filtering; otherwise uses item defaults. */
  filter?: {
    /** If present, the id of the filter to apply */
    id: FilterIdOf<IntegrationResourceRule>;
    /** If present, filters the SelectItem `key` using this filter key */
    primaryKey: string;
  };
  /** Returns valid values for this field */
  list: (
    args: Partial<ArgsOf<Access>>
  ) => AsyncIterable<FilterableSelectItem<Filters>>;
};

export type ParameterVisibility<Access extends EmptyAccess> = {
  /** Callback to determine if an interactive modal element should be displayed */
  show?: (args: Partial<ArgsOf<Access>>) => boolean;
};

export type ParameterListing<
  Filters extends AnyFilters,
  Access extends EmptyAccess,
> = {
  /** Listing object for dynamic lookup of access parameters */
  listing: /** Automatically generate lister */
    | AccessParameterListing<Filters, Access>
    | { type: Listing.External; lister: Lister };
};

export type AccessLifecycleParameters<
  Access extends EmptyAccess = EmptyAccess,
> = {
  [K in ListedKeysOf<Access>]: ParameterListing<AnyFilters, Access>;
} & {
  [K in keyof Access["parameters"]]?: ParameterVisibility<Access>;
};

/** Converts the requested access into the delegated access arguments */
type DelegationTransform<
  Access extends EmptyAccess,
  ToDelegate extends Delegate<any, any>,
> = (
  args: RequestOf<Access>["permission"],
  requestData: NewRequestData
) => Promise<ArgsOf<ToDelegate["accesses"][ToDelegate["key"]]>>;

export type AccessLifecycle<Access extends EmptyAccess> = {
  /** Determines when and how delegation occurs */
  delegation: {
    use?: (
      request: Omit<RequestOf<Access>, "delegation">
    ) => (keyof Access["delegation"] & string) | undefined;
    transforms?: {
      [D in keyof Access["delegation"]]?: DelegationTransform<
        Access,
        Access["delegation"][D]
      >;
    };
    /**
     * TODO: This is for temporary demo/lab purposes and should be removed
     * See postgres role lifecycle for an explanation
     */
    identityOverride?: (identity: string) => string;
  };

  /** Resolves to true if and only if the principal already has the requested grant */
  hasGrant: LifecycleFunction<Access, boolean>;

  /** If provided and resolves to true, skips granting or revoking delegated access.
   *
   * Use this to avoid touching shared delegated infrastructure when
   * another open request for the same principal already exists.
   */
  skipDelegate?: (
    request: StagedRequestOf<Access>,
    identity: string
  ) => Promise<boolean>;

  /** Returns the user identifier created by this lifecycle (for use by delegators)
   *
   * If missing just return the previous delegate's identity.
   */
  identifyUser?: (request: StagedRequestOf<Access>) => Promise<string>;

  /** Describes interactive options for parameters */
  parameters: AccessLifecycleParameters<Access>;

  /** Converts the user-presented access arguments into a normalized set of request parameters */
  normalize: NormalizeRequestFunction<Access>;

  /** Stages this access request
   *
   * Determines any P0-generated data necessary to grant and revoke this access. For example,
   * may look up resource identifiers from user arguments.
   */
  stage: StageRequestFunction<Access>;

  /** Provisions / de-provisions this access request
   *
   * Reflects the steps taken, in order, to grant and revoke access. Steps are executed in forward
   * order to grant access, and in reverse order to revoke access.
   */
  steps: StepsFunction<Access>;
};

export type LifecycleAccesses<Accesses extends Record<string, EmptyAccess>> = {
  [K in keyof Accesses]: AccessLifecycle<Accesses[K]>;
};

export type ContextFromArgs<
  Context extends object,
  Accesses extends EmptyAccesses,
> = (
  args: Partial<ArgsOf<Accesses[keyof Accesses]>>,
  data: NewRequestData
) => Context;

type BaseAccessLifecycleProvider<
  Context extends object,
  Accesses extends EmptyAccesses,
> = {
  contextFromArgs: ContextFromArgs<Context, Accesses>;
  contextFromRequest: (
    request:
      | RequestOf<Accesses[keyof Accesses]>
      | StagedRequestOf<Accesses[keyof Accesses]>
  ) => Context;
  filterMatchingEvidence?: FilterMatchingEvidence;
  isPreapproved?: IsPreapprovedRequest<Context, Accesses>;
};

export type AccessLifecycleProvider<
  Context extends object,
  Accesses extends EmptyAccesses,
> = BaseAccessLifecycleProvider<Context, Accesses> & {
  accesses: (context: Context) => LifecycleAccesses<Accesses>;
};

export type AccessLifecycleSpec<
  Context extends object,
  Accesses extends Record<string, EmptyAccess>,
  AccessKey extends keyof Accesses & string,
> = {
  resource: string;
  accesses: Accesses;
  key: AccessKey;
  provider: AccessLifecycleProvider<Context, Accesses>;
};

/** A function that determines if a request should be pre-approved by comparing the request
 * to existing evidence.
 *
 * @returns true if the request should be pre-approved, and false otherwise.
 */
type IsPreapprovedRequest<
  Context extends object,
  Accesses extends Record<string, EmptyAccess>,
> = (
  context: Context,
  request: AccessRequestOf<Accesses, keyof Accesses>,
  evidence: Evidence
) => boolean;

/** A function that filters evidence candidates before pre-approval matching.
 *
 * This is useful for integrations that need to perform additional validation on evidence
 * before determining if it matches a request. For example, validating routing rules for
 * break-glass access.
 *
 * @param evidence - The candidate evidence to filter
 * @param getRuleApprovals - A function to get rule approvals (includes routing rules)
 * @returns The filtered evidence that should be considered for pre-approval
 */
export type FilterMatchingEvidence = (
  evidence: Evidence[],
  getRuleApprovals: () => Promise<any>
) => Promise<Evidence[]>;

// --- DEPRECATED LEGACY DELEGATION ---

/** @deprecated Use `delegates` in access type */
type TransformDescriptor<
  /** The current access */
  Access extends EmptyAccess,
  /** The context of the delegated steps */
  ToContext extends object,
  /** The accesses of the delegated integration */
  ToAccesses extends Record<string, EmptyAccess>,
  /** The delegated access key */
  ToAccessKey extends keyof ToAccesses,
> = {
  /** The lifecycle provider */
  provider: AccessLifecycleProvider<ToContext, ToAccesses>;
  /** The delegated access key */
  access: ToAccessKey;
  /** The delegated access request */
  toRequest: (
    fromRequest: StagedRequestOf<Access>
  ) => StagedRequestOf<ToAccesses[ToAccessKey]>;
  /** Filter predicate; if absent or true, will delegate access; if false,
   *  delegation is skipped.
   */
  predicate?: (
    fromRequest: StagedRequestOf<Access>,
    direction?: "down" | "up"
  ) => Promise<boolean>;
};

/** A function to transform steps from one lifecycle context to another.
 *
 * Note that delegation can not alter the local state.
 *
 * @deprecated Use `delegates` in access type.
 */
export type LegacyDelegateTransform<
  /** The current access */
  Access extends EmptyAccess,
  /** The context of the delegated steps */
  ToContext extends object,
  /** The accesses of the delegated integration */
  ToAccesses extends Record<string, EmptyAccess>,
  /** The delegated access key */
  ToAccessKey extends keyof ToAccesses,
> = (
  request: StagedRequestOf<Access>
) => Promise<TransformDescriptor<Access, ToContext, ToAccesses, ToAccessKey>>;

/** Like {@link LegacyDelegateTransform}, but delegation can be conditionally skipped.
 *
 * To skip delegation, return a falsey transform.
 *
 * @deprecated Use `delegates` in access type.
 */
export type OptionalLegacyDelegateTransform<
  Access extends EmptyAccess,
  ToContext extends object,
  ToAccesses extends Record<string, EmptyAccess>,
  ToAccessKey extends keyof ToAccesses,
> = (
  request: StagedRequestOf<Access>
) => Promise<
  TransformDescriptor<Access, ToContext, ToAccesses, ToAccessKey> | undefined
>;
